import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  Card,
  Input,
  Button,
  Tag,
  Space,
  Select,
  Typography,
  message,
  Tooltip,
  Modal,
  Form,
  Row,
  Col,
  Avatar,
  Tabs
} from "antd";
import { SearchOutlined, DownloadOutlined, SyncOutlined, PlusOutlined, UserOutlined, FileTextOutlined } from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import moment from "moment";

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const InvoiceManager = () => {
  const { request } = useRequest();
  const [activeTab, setActiveTab] = useState("merchant");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: "",
    invoice_type: "",
    search_id: "",
  });
  const [searchText, setSearchText] = useState("");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [form] = Form.useForm();

  // Merchants state
  const [merchants, setMerchants] = useState([]);
  const [merchantLoading, setMerchantLoading] = useState(false);
  const [merchantSearch, setMerchantSearch] = useState("");
  const [merchantPage, setMerchantPage] = useState(1);
  const [hasMoreMerchants, setHasMoreMerchants] = useState(true);
  const searchTimeoutRef = useRef(null);

  // Users state
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const userSearchTimeoutRef = useRef(null);

  // Fetch Invoices
  const fetchInvoices = useCallback((page = 1, limit = 10, currentFilters = filters, tab = activeTab) => {
    setLoading(true);
    const queryParams = new URLSearchParams({ page, limit });
    
    if (currentFilters.status) queryParams.append("status", currentFilters.status);
    if (currentFilters.invoice_type) queryParams.append("invoice_type", currentFilters.invoice_type);
    
    if (currentFilters.search_id) {
      queryParams.append("search", currentFilters.search_id);
    }

    const endpoint = tab === "merchant" ? "api/admin/invoices" : "api/admin/user-invoices";

    request({
      url: `${endpoint}?${queryParams.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setInvoices(data?.data || []);
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize: limit,
          total: data?.pagination?.total || 0,
        }));
        setLoading(false);
      },
      onError: () => {
        message.error("Failed to load invoices.");
        setLoading(false);
      }
    });
  }, [request]);

  useEffect(() => {
    fetchInvoices(1, pagination.pageSize, filters, activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleTableChange = (newPagination) => {
    fetchInvoices(newPagination.current, newPagination.pageSize, filters, activeTab);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchInvoices(1, pagination.pageSize, newFilters, activeTab);
  };

  const invoiceSearchTimeoutRef = useRef(null);

  const handleSearch = (value) => {
    setSearchText(value);
    if (invoiceSearchTimeoutRef.current) clearTimeout(invoiceSearchTimeoutRef.current);
    invoiceSearchTimeoutRef.current = setTimeout(() => {
      handleFilterChange("search_id", value);
    }, 500);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setFilters({ status: "", invoice_type: "", search_id: "" });
    setSearchText("");
    setPagination({ ...pagination, current: 1 });
  };

  // Merchants logic
  const fetchMerchants = useCallback((page = 1, search = "", append = false) => {
    setMerchantLoading(true);
    const params = new URLSearchParams({ page, limit: 10 });
    if (search) params.append("search", search);

    request({
      url: `/api/admin/merchant-profile?${params.toString()}`,
      method: "GET",
      onSuccess: (res) => {
        const newData = res?.data?.data || res?.data || [];
        if (append) {
          setMerchants((prev) => [...prev, ...newData]);
        } else {
          setMerchants(newData);
        }
        const totalPages = res?.pagination?.totalPages || res?.data?.pagination?.totalPages || 1;
        setHasMoreMerchants(page < totalPages);
        setMerchantLoading(false);
      },
      onError: () => {
        message.error("Failed to fetch merchants");
        setMerchantLoading(false);
      },
    });
  }, [request]);

  const handleMerchantSearch = (value) => {
    setMerchantSearch(value);
    setMerchantPage(1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchMerchants(1, value, false);
    }, 500);
  };

  const handleMerchantScroll = (e) => {
    const { target } = e;
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 5 && hasMoreMerchants && !merchantLoading) {
      const nextPage = merchantPage + 1;
      setMerchantPage(nextPage);
      fetchMerchants(nextPage, merchantSearch, true);
    }
  };

  // Users logic
  const fetchUsers = useCallback((page = 1, search = "", append = false) => {
    setUserLoading(true);
    const params = new URLSearchParams({ page, limit: 10 });
    if (search) params.append("search", search);

    request({
      url: `/api/admin/users?${params.toString()}`,
      method: "GET",
      onSuccess: (res) => {
        const newData = res?.data || [];
        if (append) {
          setUsers((prev) => [...prev, ...newData]);
        } else {
          setUsers(newData);
        }
        const totalPages = res?.pagination?.totalPages || 1;
        setHasMoreUsers(page < totalPages);
        setUserLoading(false);
      },
      onError: () => {
        message.error("Failed to fetch users");
        setUserLoading(false);
      },
    });
  }, [request]);

  const handleUserSearch = (value) => {
    setUserSearch(value);
    setUserPage(1);
    if (userSearchTimeoutRef.current) clearTimeout(userSearchTimeoutRef.current);
    userSearchTimeoutRef.current = setTimeout(() => {
      fetchUsers(1, value, false);
    }, 500);
  };

  const handleUserScroll = (e) => {
    const { target } = e;
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 5 && hasMoreUsers && !userLoading) {
      const nextPage = userPage + 1;
      setUserPage(nextPage);
      fetchUsers(nextPage, userSearch, true);
    }
  };

  useEffect(() => {
    if (isModalVisible) {
      if (activeTab === "merchant" && merchants.length === 0) {
        setMerchantSearch("");
        setMerchantPage(1);
        fetchMerchants(1, "", false);
      } else if (activeTab === "user" && users.length === 0) {
        setUserSearch("");
        setUserPage(1);
        fetchUsers(1, "", false);
      }
    }
  }, [isModalVisible, activeTab]);

  const handleGenerateInvoice = async (values) => {
    setGenerateLoading(true);
    const endpoint = activeTab === "merchant" ? "api/admin/invoices/generate" : "api/admin/user-invoices/generate";
    const data = activeTab === "merchant" ? { merchant_id: values.merchant_id } : { user_id: values.user_id };

    request({
      url: endpoint,
      method: "POST",
      data,
      onSuccess: (res) => {
        message.success(res.message || "Invoice generated successfully");
        setIsModalVisible(false);
        form.resetFields();
        fetchInvoices(1, pagination.pageSize, filters, activeTab);
      },
      onError: (err) => {
        message.error(err?.response?.data?.message || "Failed to generate invoice");
      },
    });
    setGenerateLoading(false);
  };

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "invoice_number",
      key: "invoice_number",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: activeTab === "merchant" ? "Merchant" : "User",
      key: "user_merchant",
      render: (_, record) => (
        <span>
          {activeTab === "merchant" ? record.merchant_name : record.user_name} <br />
          <small className="text-muted">
            {activeTab === "merchant" ? record.merchant_code : record.user_email}
          </small>
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "invoice_type",
      key: "invoice_type",
      render: (type) => (
        <Tag color={type === "SUBSCRIPTION_FEE" ? "blue" : "purple"}>
          {type?.replace("_", " ")}
        </Tag>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (val) => `$${parseFloat(val).toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "default";
        if (status === "SENT") color = "success";
        if (status === "DRAFT") color = "warning";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Billing Period",
      key: "billing_period",
      render: (_, record) => (
        <span style={{ fontSize: "12px" }}>
          {moment(record.billing_period_start).format("DD MMM YYYY")} -{" "}
          {moment(record.billing_period_end).format("DD MMM YYYY")}
        </span>
      ),
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      render: (val) => moment(val).format("DD MMM YYYY"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          {record.pdf_url ? (
            <Tooltip title="Download PDF">
              <Button
                type="primary"
                shape="circle"
                icon={<DownloadOutlined />}
                onClick={() => window.open(record.pdf_url, "_blank")}
              />
            </Tooltip>
          ) : (
            <span className="text-muted">No PDF</span>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <>
            <FileTextOutlined style={{ marginRight: 8 }} />
            {"Invoice Manager"}
            <p style={{ marginTop: "5px", marginBottom: 0, fontSize: "14px", fontWeight: "normal", color: "#8c8c8c" }}>
              Manage and view your platform invoices
            </p>
          </>
        }
        extra={
          <div style={{ display: "flex", gap: "10px" }}>
            <Button 
              icon={<SyncOutlined />} 
              onClick={() => fetchInvoices(1, pagination.pageSize, filters, activeTab)}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
            >
              Generate Invoice
            </Button>
          </div>
        }
      >
        <Tabs activeKey={activeTab} onChange={handleTabChange} style={{ marginBottom: 16 }}>
          <Tabs.TabPane tab="Merchant Invoices" key="merchant" />
          <Tabs.TabPane tab="User Invoices" key="user" />
        </Tabs>

        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
            }}
          >
            <Search
              placeholder={activeTab === "merchant" ? "Search by Merchant ID, Name, or Email" : "Search by User ID, Name, or Email"}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={(value) => handleSearch(value)}
              allowClear
              style={{ minWidth: 200, flex: "1 1 200px" }}
            />
            <Select
              placeholder="Filter by Status"
              style={{ minWidth: 150, flex: "1 1 150px" }}
              allowClear
              value={filters.status || undefined}
              onChange={(val) => handleFilterChange("status", val)}
            >
              <Option value="DRAFT">DRAFT</Option>
              <Option value="SENT">SENT</Option>
            </Select>
            <Select
              placeholder="Filter by Type"
              style={{ minWidth: 200, flex: "1 1 200px" }}
              allowClear
              value={filters.invoice_type || undefined}
              onChange={(val) => handleFilterChange("invoice_type", val)}
            >
              <Option value="SUBSCRIPTION_FEE">SUBSCRIPTION FEE</Option>
              <Option value="TRANSACTION_FEE">TRANSACTION FEE</Option>
            </Select>
          </div>
        </div>

        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={invoices}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} ${activeTab === "merchant" ? "merchant invoices" : "user invoices"}`,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            onChange={handleTableChange}
            className="ant-border-space"
          />
        </div>
      </Card>

    <Modal
      title={`Generate ${activeTab === "merchant" ? "Merchant" : "User"} Invoice`}
      open={isModalVisible}
      onCancel={() => {
        setIsModalVisible(false);
        form.resetFields();
      }}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleGenerateInvoice}
      >
        {activeTab === "merchant" ? (
          <Form.Item
            name="merchant_id"
            label="Select Merchant"
            rules={[{ required: true, message: "Please select a Merchant" }]}
          >
            <Select
              size="large"
              loading={merchantLoading}
              showSearch
              allowClear
              placeholder="Search and select merchant"
              onSearch={handleMerchantSearch}
              onPopupScroll={handleMerchantScroll}
              filterOption={false}
              searchValue={merchantSearch}
            >
              {merchants.map((merchant) => (
                <Option key={merchant.id || merchant._id} value={merchant.id || merchant._id}>
                  <Space align="center">
                    <Avatar src={merchant.business_logo} icon={<UserOutlined />} />
                    <div style={{ lineHeight: '1.2' }}>
                      <div style={{ fontWeight: 500 }}>{merchant.business_name || "Unknown Merchant"}</div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {merchant.merchant_code} {merchant.invoicing_email ? `• ${merchant.invoicing_email}` : ""}
                      </Typography.Text>
                    </div>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
        ) : (
          <Form.Item
            name="user_id"
            label="Select User"
            rules={[{ required: true, message: "Please select a User" }]}
          >
            <Select
              size="large"
              loading={userLoading}
              showSearch
              allowClear
              placeholder="Search and select user"
              onSearch={handleUserSearch}
              onPopupScroll={handleUserScroll}
              filterOption={false}
              searchValue={userSearch}
            >
              {users.map((u) => (
                <Option key={u.id} value={u.id}>
                  <Space align="center">
                    <Avatar src={u.profile_picture} icon={<UserOutlined />} />
                    <div style={{ lineHeight: '1.2' }}>
                      <div style={{ fontWeight: 500 }}>{u.first_name} {u.last_name}</div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        ID: {u.id} • {u.email}
                      </Typography.Text>
                    </div>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
        
        <Form.Item>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={generateLoading}
            block
            size="large"
          >
            Generate Now
          </Button>
        </Form.Item>
      </Form>
    </Modal>
    </div>
  );
};

export default InvoiceManager;
